'use client';

import useSWR from 'swr';
import { sdk } from '@/sdk';
import useCart from '@/lib/hooks/useCart';
import { useStoreAndBusinessUnits } from '@/providers/store-and-business-units';
import type { ShipEstimate } from './types';

// EWM-414 — the cart/checkout ship-date estimate. The BFF composes the date
// from the delivery calendar + the BU pick days; the FIS-met DECISION is
// passed in by the caller. On the cart that's the SAME check as the "free
// instore" FIS progress bar (net items subtotal vs the BU threshold), so the
// ship line and that message can never disagree. The SWR key carries
// cartId + item count + fisMet (NOT the total — see EWM-690 W2 below), so the
// estimate re-fetches when items change or FIS flips. Returns null for an
// empty cart / before ready.
const useShipEstimate = (fisMet: boolean): { shipEstimate: ShipEstimate | null; isLoading: boolean } => {
  const { selectedBusinessUnit, selectedStore } = useStoreAndBusinessUnits();
  const { cart } = useCart(selectedBusinessUnit?.key, selectedStore?.key);

  const itemCount = cart?.lineItems?.length ?? 0;
  // EWM-690 W2 — the key deliberately EXCLUDES cart.sum. The estimate is
  // total-independent (calendar + BU pick days + fisMet only — see the
  // fetcher note below), so keying on `sumCents` churned a redundant fetch
  // every checkout load: the on-load applyFreight write adds freight into
  // cart.sum, re-keying this hook and re-firing an identical estimate.
  const key =
    cart?.cartId && itemCount > 0 ? (['/action/cart/getShipEstimate', cart.cartId, itemCount, fisMet] as const) : null;

  const { data, isLoading } = useSWR<ShipEstimate | null>(
    key,
    async () => {
      const res = await sdk.callAction<ShipEstimate | null>({
        actionName: 'cart/getShipEstimate',
        payload: { fisMet },
      });
      if (res.isError) throw res.error;
      return res.data ?? null;
    },
    // keepPreviousData — hold the value across any remaining key change (item
    // count / FIS flip) so the rush card / schedule strip never blanks mid-
    // refetch. (EWM-690 W2 removed `sumCents` from the key, so applying freight
    // no longer re-keys this at all — the estimate is total-independent:
    // fisMet + BU pick days + delivery calendar.)
    { revalidateOnFocus: false, keepPreviousData: true },
  );

  return { shipEstimate: data ?? null, isLoading: !!isLoading };
};

export default useShipEstimate;
